import * as Sentry from "@sentry/react";
import { routerHistory } from "../routerHistory";
import {
  installStaleChunkReloadHandler,
  isChunkLoadError,
  STATIC_CHUNK_FILE_PATTERN,
} from "utils/chunkReload";
import { normalizeSentryRoutePathname } from "utils/sentryRouteNormalization";
import { isFirestoreInternalAssertionMessage } from "utils/firestoreInternalAssertion";
import { isTinyMceDetachedDomErrorEvent } from "utils/tinymceDetachedDomError";
import { annotateNetworkFailureEvent } from "utils/sentryNetworkDiagnostics";

const REPLAY_ENABLED = process.env.REACT_APP_SENTRY_REPLAY_ENABLED === "true";
const isDev = process.env.NODE_ENV === "development";

const FIREBASE_TELEMETRY_URL_PATTERN =
  /googleapis\.com|firebaseio\.com|firebaseapp\.com|firebasestorage\.googleapis\.com/i;

const INVARIANT_FAILED_MESSAGE = "Invariant failed";
const CONTENT_MANAGER_SURFACE_PATTERN =
  /content-manager|present-calendar|present-calendar-box|present-calendar-day-body/i;

function isTracingEnabled() {
  return process.env.REACT_APP_SENTRY_TRACING_ENABLED !== "false";
}

export function getSentryEnvironment() {
  return process.env.REACT_APP_ENV || process.env.NODE_ENV || "unknown";
}

export function getSentryTracesSampleRate() {
  if (!isTracingEnabled()) {
    return 0;
  }

  const environment = getSentryEnvironment();
  if (environment === "development") {
    return 0;
  }
  if (environment === "staging") {
    return 0;
  }
  if (environment === "production") {
    return 0.05;
  }

  return 0;
}

export function isSentryReplayEnabled() {
  return REPLAY_ENABLED;
}

export function isSentryTracingEnabled() {
  return isTracingEnabled() && getSentryTracesSampleRate() > 0;
}

function shouldCreateSpanForRequest(url) {
  if (FIREBASE_TELEMETRY_URL_PATTERN.test(url)) {
    return false;
  }
  return true;
}

function getOpaqueSerializedMessage(event) {
  return String(event?.extra?.__serialized__?.message || "");
}

function getOpaqueInvariantTargetHint(event) {
  const args = Array.isArray(event?.extra?.arguments) ? event.extra.arguments : [];
  const browserEvent = args.find((arg) => arg && typeof arg === "object") || {};
  return [
    browserEvent.type,
    browserEvent.target,
    browserEvent.currentTarget,
    event?.transaction,
    event?.request?.url,
  ]
    .filter(Boolean)
    .map((value) => String(value))
    .join(" | ")
    .slice(0, 240);
}

export function isOpaqueInvariantFailedEvent(event) {
  const exception = event?.exception?.values?.[0];
  return (
    getOpaqueSerializedMessage(event) === INVARIANT_FAILED_MESSAGE
    || (
      String(exception?.value || "").includes("Object captured as exception")
      && String(exception?.value || "").includes("message")
      && getOpaqueSerializedMessage(event) === INVARIANT_FAILED_MESSAGE
    )
  );
}

export function resolveOpaqueInvariantInteractionSurface(event) {
  const targetHint = getOpaqueInvariantTargetHint(event);
  if (CONTENT_MANAGER_SURFACE_PATTERN.test(targetHint)) {
    return "content_manager";
  }
  return "unknown";
}

export function annotateOpaqueInvariantFailedEvent(event) {
  if (!isOpaqueInvariantFailedEvent(event)) {
    return event;
  }

  const interactionSurface = resolveOpaqueInvariantInteractionSurface(event);

  return {
    ...event,
    tags: {
      ...event.tags,
      opaque_exception: "true",
      invariant_failed: "true",
      interaction_surface: interactionSurface,
    },
    contexts: {
      ...event.contexts,
      opaque_invariant_failed: {
        serializedMessage: INVARIANT_FAILED_MESSAGE,
        interactionSurface,
        targetHint: getOpaqueInvariantTargetHint(event),
      },
    },
  };
}

// After a deploy, requests for old hashed chunks hit the SPA fallback and get
// index.html, which the browser then evaluates as JS and reports via the global
// onerror handler as "SyntaxError: Unexpected token '<'". LazyRouteErrorBoundary
// and installStaleChunkReloadHandler already handle this case (auto-reload, and a
// tagged capture if reloading loops), so the raw global event is pure noise.
export function isStaleChunkSyntaxErrorEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception || exception.type !== "SyntaxError") {
    return false;
  }
  if (!String(exception.value || "").includes("Unexpected token '<'")) {
    return false;
  }

  const frames = exception.stacktrace?.frames || [];
  const topFrame = frames[frames.length - 1];
  return STATIC_CHUNK_FILE_PATTERN.test(topFrame?.filename || topFrame?.abs_path || "");
}

export function isStaleChunkLoadErrorEvent(event) {
  if (event?.tags?.chunk_reload_loop === "true") {
    return false;
  }
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  return isChunkLoadError({ name: exception.type, message: exception.value });
}

// Facebook/Instagram Android in-app browsers inject navigation_performance_logger_android,
// which throws when its Java bridge is torn down (often on beforeunload / navigation).
// Unactionable third-party noise; not caused by our app code.
export function isFacebookInAppBrowserBridgeErrorEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  const type = String(exception.type || "");
  const value = String(exception.value || "");
  if (
    value.includes("Java object is gone")
    || value.includes("Java bridge method invocation error")
  ) {
    return true;
  }

  // Facebook iOS IAB / pixel bridge: postMessage to a torn-down native handler.
  const isInvalidAccess =
    type === "InvalidAccessError"
    || value.includes("InvalidAccessError")
    || value.includes("does not support the operation or argument");
  if (!isInvalidAccess) {
    return false;
  }
  const frames = exception.stacktrace?.frames || [];
  return frames.some((frame) => {
    const fn = String(frame?.function || "");
    const file = String(frame?.filename || frame?.abs_path || "");
    return (
      fn.includes("dispatchToBridge")
      || fn.includes("sendPostMessage")
      || file.includes("connect.facebook.net")
      || file.includes("fbevents.js")
    );
  });
}

// Browser extensions serialize React-owned DOM nodes (often anchors) and hit
// JSON.stringify circular-structure errors on __reactFiber. Anonymous frames,
// marketing/auth surfaces, no app culprit — unactionable inject noise.
export function isExtensionReactFiberCircularJsonErrorEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  const value = String(exception.value || "");
  if (!value.includes("Converting circular structure to JSON")) {
    return false;
  }
  if (!value.includes("HTMLAnchorElement") || !value.includes("__reactFiber")) {
    return false;
  }
  const frames = exception.stacktrace?.frames || [];
  if (frames.length === 0) {
    return true;
  }
  return frames.every((frame) => {
    const file = String(frame?.filename || frame?.abs_path || "");
    return (
      !file
      || file === "<anonymous>"
      || file.includes("react-dom")
    );
  });
}

// WKWebView / iOS in-app browsers and some embedded webviews probe
// window.webkit.messageHandlers. When the bridge is missing or torn down,
// evaluating that path throws TypeError. Third-party inject noise, not app code.
export function isWebkitMessageHandlersBridgeErrorEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  const value = String(exception.value || "");
  return (
    value.includes("window.webkit.messageHandlers")
    || value.includes("webkit.messageHandlers")
  );
}

// Firestore persistentLocalCache opens IndexedDB and runs transactions that the
// browser can abort or fail for reasons outside app control: private storage
// limits, tab suspension, IAB quirks, user clearing site data, disk full, or a
// corrupted backing store. Firebase surfaces these as IndexedDbTransactionError
// (often wrapped in FirebaseError query/write failures). There is no app-level
// recovery path beyond falling back to network-only reads, which Firestore already
// does — keep these out of Sentry so real product bugs stay visible.
export function isFirestoreIndexedDbAbortErrorEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  const type = String(exception.type || "");
  const value = String(exception.value || "");

  const isIndexedDbTransactionError =
    type === "IndexedDbTransactionError"
    || value.includes("IndexedDbTransactionError");

  if (isIndexedDbTransactionError) {
    return true;
  }

  // Raw browser IndexedDB failures that accompany Firestore persistence outages.
  // Require an IndexedDB / object-store signal so we do not drop unrelated AbortErrors.
  const looksLikeIndexedDb =
    /indexed\s*db|indexeddb|object store|createObjectStore|backing store/i.test(value)
    || /indexeddb\.leveldb/i.test(value);

  if (!looksLikeIndexedDb) {
    return false;
  }

  return (
    value.includes("AbortError")
    || value.includes("The operation was aborted")
    || value.includes("Database deleted by request of the user")
    || value.includes("Object store cannot be found")
    || value.includes("Unable to open database file on disk")
    || value.includes("Attempt to get records from database without an in-progress transaction")
    || value.includes("Connection to Indexed Database server lost")
    || value.includes("Internal error opening backing store")
    || value.includes("FILE_ERROR_NO_SPACE")
    || value.includes("createObjectStore")
    || type === "QuotaExceededError"
    || value.includes("QuotaExceededError")
  );
}

// Firestore tears down its client on pagehide / auth teardown. Late listeners then
// reject with "The client has already been terminated." — unload noise, not a bug.
export function isFirestoreClientTerminatedErrorEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  return String(exception.value || "").includes("The client has already been terminated");
}

// Re-export for existing sentryClient.test.js imports.
export { isTinyMceDetachedDomErrorEvent };

// Once Firestore's AsyncQueue fails, the SDK's pagehide / visibilitychange
// listeners throw INTERNAL ASSERTION FAILED on the next enqueue. That is an
// unrecoverable client state (reload required), not an actionable app bug.
// Bootstrap already surfaces the user-facing failure; these listener throws are noise.
export function isFirestoreInternalAssertionErrorEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  return isFirestoreInternalAssertionMessage(exception.value);
}

// Browser extensions (Grammarly, password managers, etc.) reject promises with
// this opaque string when a content-script bridge target disappears. No stack,
// no app frames — pure third-party noise.
export function isBrowserExtensionObjectNotFoundRejectionEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  const value = String(exception.value || "");
  return (
    value.includes("Object Not Found Matching Id:")
    && value.includes("MethodName:")
  );
}

// Chrome extension messaging API leaked into the page when a tab/frame is gone.
export function isChromeExtensionSendMessageTabNotFoundEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  const value = String(exception.value || "");
  return (
    value.includes("runtime.sendMessage")
    && value.includes("Tab not found")
  );
}

// In-app browsers (TikTok, etc.) inject perf probes that read timing.domInteractive
// on an undefined performance entry. Not app code.
export function isInjectedDomInteractivePerfProbeErrorEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  const value = String(exception.value || "");
  return (
    value.includes("domInteractive")
    && (
      value.includes("Cannot read properties of undefined")
      || value.includes("undefined is not an object")
    )
  );
}

// Some browser extensions inject /asset/asset.js (switchboard / triggerAnalysis)
// which loops on TypeError: Cannot read properties of null (reading 'concat').
// Entire stack is third-party; floods Sentry while the tab stays open.
export function isInjectedAssetJsNullConcatErrorEvent(event) {
  const exception = event?.exception?.values?.[0];
  if (!exception) {
    return false;
  }
  const value = String(exception.value || "");
  const isNullConcat =
    value.includes("reading 'concat'")
    || value.includes('reading "concat"')
    || /null is not an object \(evaluating '.*\.concat'\)/i.test(value);
  if (!isNullConcat) {
    return false;
  }
  const frames = exception.stacktrace?.frames || [];
  return frames.some((frame) => {
    const file = String(frame?.filename || frame?.abs_path || "");
    return file.includes("/asset/asset.js");
  });
}

export function initSentry() {
  // Install even when Sentry is disabled so stale-tab chunk failures still recover.
  installStaleChunkReloadHandler();

  if (!process.env.REACT_APP_SENTRY_DSN) {
    return;
  }

  const integrations = [];

  if (isTracingEnabled() && getSentryTracesSampleRate() > 0) {
    integrations.push(
      Sentry.reactRouterV5BrowserTracingIntegration({
        history: routerHistory,
        beforeStartSpan: (options) => ({
          ...options,
          name: normalizeSentryRoutePathname(options.name),
        }),
        shouldCreateSpanForRequest,
      }),
    );
  }

  if (REPLAY_ENABLED) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        // Do not set networkDetailAllowUrls.
        // Request/response body capture only becomes relevant if network detail URLs are explicitly allowlisted.
        // If we ever add an allowlist, set networkCaptureBodies: false unless there is a reviewed safe reason.
        // Do not add replayCanvasIntegration.
        block: [
          "[data-sentry-block]",
          ".sentry-block",
          ".ll-roster-box",
          ".ll-camera-widget",
          ".ll-randomizer-widget",
          ".ll-whiteboard-widget",
          ".ll-settings-page",
          ".ll-subscription-page",
          ".ll-admin-surface",
        ],
      }),
    );
  }

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: getSentryEnvironment(),
    release: process.env.REACT_APP_RELEASE || undefined,
    integrations,
    tracesSampler: () => getSentryTracesSampleRate(),
    beforeSend: (event) => {
      const annotatedEvent = annotateOpaqueInvariantFailedEvent(
        annotateNetworkFailureEvent(event)
      );
      return (
        isStaleChunkSyntaxErrorEvent(annotatedEvent)
        || isStaleChunkLoadErrorEvent(annotatedEvent)
        || isFacebookInAppBrowserBridgeErrorEvent(annotatedEvent)
        || isWebkitMessageHandlersBridgeErrorEvent(annotatedEvent)
        || isFirestoreIndexedDbAbortErrorEvent(annotatedEvent)
        || isFirestoreClientTerminatedErrorEvent(annotatedEvent)
        || isFirestoreInternalAssertionErrorEvent(annotatedEvent)
        || isBrowserExtensionObjectNotFoundRejectionEvent(annotatedEvent)
        || isChromeExtensionSendMessageTabNotFoundEvent(annotatedEvent)
        || isInjectedDomInteractivePerfProbeErrorEvent(annotatedEvent)
        || isInjectedAssetJsNullConcatErrorEvent(annotatedEvent)
        || isExtensionReactFiberCircularJsonErrorEvent(annotatedEvent)
        || isTinyMceDetachedDomErrorEvent(annotatedEvent)
          ? null
          : annotatedEvent
      );
    },

    // Production: error-only replay (buffer up to ~1 min before an error, no full sessions).
    // Dev/staging with flag on: sample full sessions locally to make privacy QA easier.
    replaysSessionSampleRate: isDev && REPLAY_ENABLED ? 1.0 : 0.0,
    replaysOnErrorSampleRate: REPLAY_ENABLED ? 1.0 : 0.0,
  });
}
